import { useCallback, useEffect, useState } from 'react';
import { useImageUrl } from '../api/useImageUrl';
import { useTasks } from '../api/useTasks';

/**
 * Which classes get taken for which.
 *
 * Overall accuracy says there is a problem. This says where, and therefore where
 * an expert's hour is worth spending — which is the scarce resource in this whole
 * project.
 *
 * Two things the page insists on:
 *
 * A CELL IS CLICKABLE. "Eleven hams were called shoulders" is a number; the
 * eleven images are the explanation, and in practice half of them turn out to be
 * mislabelled rather than misclassified. A matrix you cannot open is a matrix
 * that gets believed.
 *
 * THE SAMPLE IS DECLARED. In assisted mode a person only looks at crates the
 * model already rejected, so the matrix describes its hard cases and reads as
 * catastrophic. The same table in shadow mode is unbiased. They look identical,
 * so the page says which one it is instead of leaving it to be worked out.
 */

interface Klass {
  id: number;
  code: string;
  name: string;
  family: string | null;
}

interface Cell {
  actual: number;
  predicted: number;
  n: number;
}

interface PerClass {
  classId: number;
  code: string;
  name: string;
  support: number;
  recall: number | null;
  precision: number | null;
}

interface Result {
  classes: Klass[];
  cells: Cell[];
  perClass: PerClass[];
  confusable: { a: { classId: number; name: string }; b: { classId: number; name: string }; confusions: number }[];
  labelled: number;
  inspections: number;
  byDecision: Record<string, number>;
  accuracy: number | null;
  biased: boolean;
  labelledRejectShare: number;
  caveat: string | null;
}

interface Crate {
  id: string;
  imageKey: string;
  time: string;
  confidence: number | null;
  decision: string | null;
}

const PERIODS = [
  { key: '7', label: '7 days' },
  { key: '30', label: '30 days' },
  { key: '90', label: '90 days' },
  { key: '', label: 'All' },
] as const;

export function ConfusionView() {
  const { products, taskFor } = useTasks();
  const classifyProducts = products.filter((p) => taskFor(p.id).usesClasses);

  const [productId, setProductId] = useState<number | null>(null);
  const [days, setDays] = useState<string>('30');
  const [data, setData] = useState<Result | null>(null);
  const [open, setOpen] = useState<{ actual: Klass; predicted: Klass } | null>(null);
  const [crates, setCrates] = useState<Crate[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (productId === null && classifyProducts.length > 0) setProductId(classifyProducts[0]!.id);
  }, [classifyProducts, productId]);

  const load = useCallback(async () => {
    if (!productId) return;
    const from = days
      ? new Date(Date.now() - Number(days) * 86_400_000).toISOString()
      : undefined;
    try {
      const res = await fetch(
        `/api/products/${productId}/confusion${from ? `?from=${encodeURIComponent(from)}` : ''}`,
      );
      if (!res.ok) throw new Error((await res.json()).message ?? 'Could not load');
      setData(await res.json());
    } catch (e) {
      setError((e as Error).message);
    }
  }, [productId, days]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCell = async (actual: Klass, predicted: Klass) => {
    setOpen({ actual, predicted });
    setCrates([]);
    const res = await fetch(
      `/api/products/${productId}/confusion/images?actual=${actual.id}&predicted=${predicted.id}`,
    );
    if (res.ok) setCrates(await res.json());
  };

  if (classifyProducts.length === 0) {
    return (
      <div>
        <h1 className="view-title">Confusion</h1>
        <div className="panel muted">
          No classification product yet. This page compares what a model predicted against what a
          person said it really was.
        </div>
      </div>
    );
  }

  const at = (a: number, p: number) => data?.cells.find((c) => c.actual === a && c.predicted === p)?.n ?? 0;
  const rowTotal = (a: number) => data?.cells.filter((c) => c.actual === a).reduce((n, c) => n + c.n, 0) ?? 0;
  const worst = Math.max(1, ...(data?.cells.filter((c) => c.actual !== c.predicted).map((c) => c.n) ?? [0]));

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <h1 className="view-title">Confusion</h1>
        <div className="row" style={{ gap: 8 }}>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              className={`btn sm${days === p.key ? ' primary' : ''}`}
              onClick={() => setDays(p.key)}
            >
              {p.label}
            </button>
          ))}
          <select value={productId ?? ''} onChange={(e) => setProductId(Number(e.target.value))}>
            {classifyProducts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 12 }}>{error}</div>}

      {data && (
        <>
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="row" style={{ gap: 26, flexWrap: 'wrap' }}>
              <span>
                <strong style={{ fontSize: 22 }}>
                  {data.accuracy === null ? '—' : `${(data.accuracy * 100).toFixed(1)}%`}
                </strong>{' '}
                <span className="muted">accuracy on labelled crates</span>
              </span>
              <span>
                <strong style={{ fontSize: 22 }}>{data.labelled.toLocaleString('nl-NL')}</strong>{' '}
                <span className="muted">labelled of {data.inspections.toLocaleString('nl-NL')}</span>
              </span>
              {Object.entries(data.byDecision).map(([k, n]) => (
                <span key={k}>
                  <strong style={{ fontSize: 22 }}>{n.toLocaleString('nl-NL')}</strong>{' '}
                  <span className="muted">{k}</span>
                </span>
              ))}
            </div>
            {data.caveat && (
              <div
                style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  borderRadius: 6,
                  background: data.biased ? 'rgba(249,115,22,.12)' : 'rgba(148,163,184,.12)',
                  border: data.biased ? '1px solid rgba(249,115,22,.4)' : 'none',
                  fontSize: 13,
                  lineHeight: 1.55,
                }}
              >
                {data.biased && <strong>⚠ These numbers are not the line's accuracy. </strong>}
                {data.caveat}
                {data.biased && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {(data.labelledRejectShare * 100).toFixed(0)}% of what was labelled had already
                    been rejected by the model.
                  </div>
                )}
              </div>
            )}
          </div>

          {data.confusable.length > 0 && (
            <div className="panel" style={{ marginBottom: 16 }}>
              <h3 style={{ marginTop: 0, marginBottom: 8 }}>Worth verifying first</h3>
              <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                Pairs, not directions: mistaking A for B and B for A is one problem with one cause,
                and splitting it across two rows halves its apparent size.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {data.confusable.slice(0, 8).map((p) => (
                  <span
                    key={`${p.a.classId}-${p.b.classId}`}
                    style={{
                      fontSize: 13,
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      padding: '5px 10px',
                    }}
                  >
                    {p.a.name} ↔ {p.b.name}{' '}
                    <strong style={{ color: 'var(--warn, #f59e0b)' }}>{p.confusions}×</strong>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="panel" style={{ marginBottom: 16, overflowX: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>Actual (rows) against predicted (columns)</h3>
            <table style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }} />
                  {data.classes.map((c) => (
                    <th
                      key={c.id}
                      title={c.name}
                      style={{ padding: '6px 8px', writingMode: 'vertical-rl', textAlign: 'left' }}
                    >
                      {c.code}
                    </th>
                  ))}
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>n</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>recall</th>
                </tr>
              </thead>
              <tbody>
                {data.classes.map((row) => {
                  const total = rowTotal(row.id);
                  const rc = data.perClass.find((p) => p.classId === row.id);
                  return (
                    <tr key={row.id}>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }} title={row.name}>
                        <strong>{row.code}</strong>
                      </td>
                      {data.classes.map((col) => {
                        const n = at(row.id, col.id);
                        const diag = row.id === col.id;
                        return (
                          <td
                            key={col.id}
                            onClick={() => n > 0 && void openCell(row, col)}
                            title={
                              n === 0
                                ? ''
                                : `${n} crate(s) that were ${row.name}, called ${col.name} — click to see them`
                            }
                            style={{
                              padding: '5px 8px',
                              textAlign: 'center',
                              cursor: n > 0 ? 'pointer' : 'default',
                              fontVariantNumeric: 'tabular-nums',
                              // Diagonal green, mistakes orange in proportion to
                              // the worst cell — so the eye lands on the problem
                              // rather than on the biggest class.
                              background: !n
                                ? 'transparent'
                                : diag
                                  ? 'rgba(34,197,94,.16)'
                                  : `rgba(249,115,22,${0.15 + 0.55 * (n / worst)})`,
                              color: n ? 'var(--text)' : 'var(--text-dim)',
                            }}
                          >
                            {n || '·'}
                          </td>
                        );
                      })}
                      <td style={{ padding: '5px 8px', textAlign: 'right' }}>{total}</td>
                      <td
                        style={{
                          padding: '5px 8px',
                          textAlign: 'right',
                          color:
                            rc?.recall === null
                              ? 'var(--text-dim)'
                              : (rc?.recall ?? 1) < 0.9
                                ? 'var(--warn, #f59e0b)'
                                : 'var(--good)',
                        }}
                      >
                        {rc?.recall === null ? 'not measured' : `${((rc?.recall ?? 0) * 100).toFixed(0)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="muted" style={{ fontSize: 12, marginTop: 10 }}>
              Click any cell to see the crates behind it. A class with no labelled crates reads “not
              measured” rather than 0% — zero would make it look broken instead of unchecked.
            </p>
          </div>
        </>
      )}

      {open && (
        <div className="panel">
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
            <h3 style={{ margin: 0 }}>
              Really <strong>{open.actual.name}</strong>, called{' '}
              <strong>{open.predicted.name}</strong> — {crates.length} crate(s)
            </h3>
            <button className="btn sm" onClick={() => setOpen(null)}>
              Close
            </button>
          </div>
          {open.actual.id === open.predicted.id && (
            <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
              These are the ones it got right. Worth a look when a class scores suspiciously well —
              it sometimes means the label, not the model, is doing the work.
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))', gap: 10 }}>
            {crates.map((c) => (
              <Thumb key={c.id} crate={c} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Thumb({ crate }: { crate: Crate }) {
  const url = useImageUrl(crate.imageKey);
  return (
    <div>
      <div style={{ aspectRatio: '1', background: 'var(--bg-panel-2)', borderRadius: 6, overflow: 'hidden' }}>
        {url && <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
      </div>
      <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
        {crate.confidence != null ? `${(crate.confidence * 100).toFixed(0)}%` : '—'} · {crate.decision}
      </div>
    </div>
  );
}
